import type { Message, PublicUser } from '@discordo/shared';
import { useLayoutEffect, useMemo, useRef, useState, type DragEvent, type ReactElement } from 'react';

import { AttachmentView } from './Attachments';
import { MessageContent } from './MessageContent';

/**
 * Centre column: the history of the open channel, newest at the bottom.
 *
 * Two behaviours make it feel like a chat instead of a list. The view stays
 * pinned to the bottom while the reader is already there, and lets go the
 * moment they scroll up to read — a new message must never yank the page from
 * under them. And when an older page is prepended, the scroll position is
 * pushed down by exactly how much the content grew, so the line being read
 * stays where it was.
 *
 * It is also where a dragged file lands. The drop zone is the conversation and
 * not the composer because that is the target the gesture aims at: the large
 * area everyone is already looking at, rather than a one-line box at the
 * bottom edge.
 */

/** Two messages by the same author within this window share one header. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

/** How close to the bottom still counts as "reading the latest". */
const PIN_THRESHOLD_PX = 80;

/** How close to the top triggers pulling the previous page. */
const LOAD_THRESHOLD_PX = 120;

interface MessageGroup {
  key: string;
  author: PublicUser;
  messages: Message[];
}

function groupMessages(messages: Message[]): MessageGroup[] {
  const groups: MessageGroup[] = [];

  for (const message of messages) {
    const current = groups[groups.length - 1];
    const previous = current?.messages[current.messages.length - 1];
    const elapsed =
      previous === undefined
        ? Number.POSITIVE_INFINITY
        : Date.parse(message.createdAt) - Date.parse(previous.createdAt);

    if (current && previous && previous.author.id === message.author.id && elapsed <= GROUP_WINDOW_MS) {
      current.messages.push(message);
      continue;
    }

    groups.push({ key: message.id, author: message.author, messages: [message] });
  }

  return groups;
}

const timeFormat = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const dateTimeFormat = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Whether what is being dragged carries files at all. */
function carriesFiles(transfer: DataTransfer | null): boolean {
  return transfer !== null && Array.from(transfer.types).includes('Files');
}

export interface MessageListProps {
  messages: Message[];
  hasMore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  /**
   * Receives a file dropped on the conversation. Left out where there is
   * nowhere to send one, and the list then ignores every drag: highlighting a
   * target that accepts nothing is a promise the interface cannot keep.
   */
  onDropFile?: (file: File) => void;
}

export function MessageList({
  messages,
  hasMore,
  loadingOlder,
  onLoadOlder,
  onDropFile,
}: MessageListProps): ReactElement {
  const groups = useMemo(() => groupMessages(messages), [messages]);
  const [dropping, setDropping] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const lastHeight = useRef(0);
  const firstMessageId = useRef<string | null>(null);

  const currentFirstId = messages[0]?.id ?? null;

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    const grew = element.scrollHeight - lastHeight.current;
    const prepended = firstMessageId.current !== null && currentFirstId !== firstMessageId.current;

    if (prepended && grew > 0) {
      element.scrollTop += grew;
    } else if (pinnedToBottom.current) {
      element.scrollTop = element.scrollHeight;
    }

    firstMessageId.current = currentFirstId;
    lastHeight.current = element.scrollHeight;
  }, [messages, currentFirstId]);

  function handleScroll(): void {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    pinnedToBottom.current =
      element.scrollHeight - element.scrollTop - element.clientHeight < PIN_THRESHOLD_PX;

    if (element.scrollTop < LOAD_THRESHOLD_PX && hasMore && !loadingOlder) {
      onLoadOlder();
    }
  }

  /**
   * `preventDefault` on drag-over is what makes the element a drop target at
   * all: without it the browser refuses the drop and opens the file in the tab
   * instead, throwing away whatever was on screen.
   */
  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    if (onDropFile === undefined || !carriesFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    setDropping(true);
  }

  function handleDragLeave(): void {
    setDropping(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    if (onDropFile === undefined) {
      return;
    }

    event.preventDefault();
    setDropping(false);

    // One at a time: a single progress bar cannot tell the truth about two
    // uploads, and the rest of the selection would upload invisibly.
    const file = event.dataTransfer?.files[0];
    if (file) {
      onDropFile(file);
    }
  }

  return (
    <div
      className={dropping ? 'message-scroll is-dropping' : 'message-scroll'}
      ref={scrollRef}
      onScroll={handleScroll}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {hasMore ? (
        <p className="message-history-hint">
          {loadingOlder ? 'Carregando mensagens anteriores…' : 'Role para cima para ver o histórico.'}
        </p>
      ) : (
        <p className="message-history-hint">Este é o começo da conversa neste canal.</p>
      )}

      {groups.map((group) => (
        <article className="message-group" key={group.key}>
          <span className="avatar" aria-hidden="true">
            {group.author.displayName.slice(0, 1).toUpperCase()}
          </span>

          <div className="message-group-body">
            <header className="message-group-header">
              <span className="message-author">{group.author.displayName}</span>
              <time className="message-time" dateTime={group.messages[0]?.createdAt}>
                {dateTimeFormat.format(new Date(group.messages[0]?.createdAt ?? 0))}
              </time>
            </header>

            {group.messages.map((message) => (
              <div className="message-row" key={message.id}>
                <time className="message-row-time" dateTime={message.createdAt}>
                  {timeFormat.format(new Date(message.createdAt))}
                </time>
                <MessageContent content={message.content} />
                {message.editedAt !== null && <span className="message-edited">(editada)</span>}
                {message.attachments.length > 0 && (
                  <div className="message-attachments">
                    {message.attachments.map((attachment) => (
                      <AttachmentView key={attachment.id} attachment={attachment} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

export default MessageList;
